import React, {useEffect, useState} from "react";
import "./Resume.css";
import SteamCard, {steamHeaderUrl, steamStoreUrl} from "../SteamCard";

type Lang = "ru" | "en";
type L = Record<Lang, string>;
type LList = Record<Lang, string[]>;

interface Role {
    title: string;
    project?: string;
    release?: L;
    url?: string;
    steamAppId?: string;
    steamHeader?: string;
    description?: L;
    highlights?: LList;
}

interface Job {
    company: string;
    period?: L;
    note?: L;
    roles: Role[];
}

const LANG_KEY = "resumeLang";

function initialLang(): Lang {
    try {
        const saved = localStorage.getItem(LANG_KEY);
        if (saved === "ru" || saved === "en") return saved;
    } catch {
        // Storage can be unavailable (private mode); fall back to the browser language
    }
    const browserLang = typeof navigator !== "undefined" ? navigator.language : "en";
    return browserLang.toLowerCase().startsWith("ru") ? "ru" : "en";
}

const UnityIcon = () => (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
        <path d="m12.9288 4.2939 3.7997 2.1929c.1366.077.1415.2905 0 .3675l-4.515 2.6076a.4192.4192 0 0 1-.4246 0L7.274 6.8543c-.139-.0745-.1415-.293 0-.3675l3.7972-2.193V0L1.3758 5.5977V16.793l3.7177-2.1456v-4.3858c-.0025-.1565.1813-.2682.318-.1838l4.5148 2.6076a.4252.4252 0 0 1 .2136.3676v5.2127c.0025.1565-.1813.2682-.3179.1838l-3.7996-2.1929-3.7178 2.1457L12 24l9.6954-5.5977-3.7178-2.1457-3.7996 2.1929c-.1341.082-.3229-.0248-.3179-.1838V13.053c0-.1565.087-.2956.2136-.3676l4.5149-2.6076c.134-.082.3228.0224.3179.1838v4.3858l3.7177 2.1456V5.5977L12.9288 0Z"/>
    </svg>
);

const CSharpIcon = () => (
    <svg viewBox="0 0 128 128" width="20" height="20">
        <path fill="#9B4F96" d="M115.4 30.7L67.1 2.9c-.8-.5-1.9-.7-3.1-.7-1.2 0-2.3.3-3.1.7l-48 27.9c-1.7 1-2.9 3.5-2.9 5.4v55.7c0 1.1.2 2.4 1 3.5l106.8-62c-.6-1.2-1.5-2.1-2.4-2.7z"/>
        <path fill="#68217A" d="M10.7 95.3c.5.8 1.2 1.5 1.9 1.9l48.2 27.9c.8.5 1.9.7 3.1.7 1.2 0 2.3-.3 3.1-.7l48-27.9c1.7-1 2.9-3.5 2.9-5.4V36.1c0-.9-.1-1.9-.6-2.8l-106.6 62z"/>
        <path fill="#fff" d="M85.3 76.1C81.1 83.5 73.1 88.5 64 88.5c-13.5 0-24.5-11-24.5-24.5s11-24.5 24.5-24.5c9.1 0 17.1 5 21.3 12.5l13-7.5c-6.8-11.9-19.6-20-34.3-20-21.8 0-39.5 17.7-39.5 39.5s17.7 39.5 39.5 39.5c14.6 0 27.4-8 34.2-19.8l-12.9-7.6zM97 66.2l.9-4.3h-4.2v-4.7h5.1L100 51h4.9l-1.2 6.1h3.8l1.2-6.1h4.8l-1.2 6.1h2.4v4.7h-3.3l-.9 4.3h4.2v4.7h-5.1l-1.2 6h-4.9l1.2-6h-3.8l-1.2 6h-4.8l1.2-6h-2.4v-4.7H97zm4.8 0h3.8l.9-4.3h-3.8l-.9 4.3z"/>
    </svg>
);

const UnrealIcon = () => (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
        <path d="M12 0a12 12 0 1012 12A12 12 0 0012 0zm0 23.52A11.52 11.52 0 1123.52 12 11.52 11.52 0 0112 23.52zm7.13-9.791c-.206.997-1.126 3.557-4.06 4.942l-1.179-1.325-1.988 2a7.338 7.338 0 01-5.804-2.978 2.859 2.859 0 00.65.123c.326.006.678-.114.678-.66v-5.394a.89.89 0 00-1.116-.89c-.92.212-1.656 2.509-1.656 2.509a7.304 7.304 0 012.528-5.597 7.408 7.408 0 013.73-1.721c-1.006.573-1.57 1.507-1.57 2.29 0 1.262.76 1.109.984.923v7.28a1.157 1.157 0 00.148.256 1.075 1.075 0 00.88.445c.76 0 1.747-.868 1.747-.868V9.172c0-.6-.452-1.324-.905-1.572 0 0 .838-.149 1.484.346a5.537 5.537 0 01.387-.425c1.508-1.48 2.929-1.902 4.112-2.112 0 0-2.151 1.69-2.151 3.96 0 1.687.043 5.801.043 5.801.799.771 1.986-.342 3.059-1.441Z"/>
    </svg>
);

const BlenderIcon = () => (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="#F5792A">
        <path d="M12.51 13.214c.046-.8.438-1.506 1.03-2.006a3.424 3.424 0 0 1 2.212-.79c.85 0 1.631.3 2.211.79.592.5.983 1.206 1.028 2.005.045.823-.285 1.586-.865 2.153a3.389 3.389 0 0 1-2.374.938 3.393 3.393 0 0 1-2.376-.938c-.58-.567-.91-1.33-.865-2.152M7.35 14.831c.006.314.106.922.256 1.398a7.372 7.372 0 0 0 1.593 2.757 8.227 8.227 0 0 0 2.787 2.001 8.947 8.947 0 0 0 3.66.76 8.964 8.964 0 0 0 3.657-.772 8.285 8.285 0 0 0 2.785-2.01 7.428 7.428 0 0 0 1.592-2.762 6.964 6.964 0 0 0 .25-3.074 7.123 7.123 0 0 0-1.016-2.779 7.764 7.764 0 0 0-1.852-2.043h.002L13.566 2.55l-.02-.015c-.492-.378-1.319-.376-1.86.002-.547.382-.609 1.015-.123 1.415l-.001.001 3.126 2.543-9.53.01h-.013c-.788.001-1.545.518-1.695 1.172-.154.665.38 1.217 1.2 1.22V8.9l4.83-.01-8.62 6.617-.034.025c-.813.622-1.075 1.658-.563 2.313.52.667 1.625.668 2.447.004L7.414 14s-.069.52-.063.831zm12.09 1.741c-.97.988-2.326 1.548-3.795 1.55-1.47.004-2.827-.552-3.797-1.538a4.51 4.51 0 0 1-1.036-1.622 4.282 4.282 0 0 1 .282-3.519 4.702 4.702 0 0 1 1.153-1.371c.942-.768 2.141-1.183 3.396-1.185 1.256-.002 2.455.41 3.398 1.175.48.391.87.854 1.152 1.367a4.28 4.28 0 0 1 .522 1.706 4.236 4.236 0 0 1-.239 1.811 4.54 4.54 0 0 1-1.035 1.626"/>
    </svg>
);

const ReactIcon = () => (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="#61DAFB">
        <path d="M14.23 12.004a2.236 2.236 0 0 1-2.235 2.236 2.236 2.236 0 0 1-2.236-2.236 2.236 2.236 0 0 1 2.235-2.236 2.236 2.236 0 0 1 2.236 2.236zm2.648-10.69c-1.346 0-3.107.96-4.888 2.622-1.78-1.653-3.542-2.602-4.887-2.602-.41 0-.783.093-1.106.278-1.375.793-1.683 3.264-.973 6.365C1.98 8.917 0 10.42 0 12.004c0 1.59 1.99 3.097 5.043 4.03-.704 3.113-.39 5.588.988 6.38.32.187.69.275 1.102.275 1.345 0 3.107-.96 4.888-2.624 1.78 1.654 3.542 2.603 4.887 2.603.41 0 .783-.09 1.106-.275 1.374-.792 1.683-3.263.973-6.365C22.02 15.096 24 13.59 24 12.004c0-1.59-1.99-3.097-5.043-4.032.704-3.11.39-5.587-.988-6.38-.318-.184-.688-.277-1.092-.278zm-.005 1.09v.006c.225 0 .406.044.558.127.666.382.955 1.835.73 3.704-.054.46-.142.945-.25 1.44-.96-.236-2.006-.417-3.107-.534-.66-.905-1.345-1.727-2.035-2.447 1.592-1.48 3.087-2.292 4.105-2.295zm-9.77.02c1.012 0 2.514.808 4.11 2.28-.686.72-1.37 1.537-2.02 2.442-1.107.117-2.154.298-3.113.538-.112-.49-.195-.964-.254-1.42-.23-1.868.054-3.32.714-3.707.19-.09.4-.127.563-.132zm4.882 3.05c.455.468.91.992 1.36 1.564-.44-.02-.89-.034-1.345-.034-.46 0-.915.01-1.36.034.44-.572.895-1.096 1.345-1.565zM12 8.1c.74 0 1.477.034 2.202.093.406.582.802 1.203 1.183 1.86.372.64.71 1.29 1.018 1.946-.308.655-.646 1.31-1.013 1.95-.38.66-.773 1.288-1.18 1.87-.728.063-1.466.098-2.21.098-.74 0-1.477-.035-2.202-.093-.406-.582-.802-1.204-1.183-1.86-.372-.64-.71-1.29-1.018-1.946.303-.657.646-1.313 1.013-1.954.38-.66.773-1.286 1.18-1.868.728-.064 1.466-.098 2.21-.098zm-3.635.254c-.24.377-.48.763-.704 1.16-.225.39-.435.782-.635 1.174-.265-.656-.49-1.31-.676-1.947.64-.15 1.315-.283 2.015-.386zm7.26 0c.695.103 1.365.23 2.006.387-.18.632-.405 1.282-.66 1.933-.2-.39-.41-.783-.64-1.174-.225-.392-.465-.774-.705-1.146zm3.063.675c.484.15.944.317 1.375.498 1.732.74 2.852 1.708 2.852 2.476-.005.768-1.125 1.74-2.857 2.475-.42.18-.88.342-1.355.493-.28-.958-.646-1.956-1.1-2.98.45-1.017.81-2.01 1.085-2.964zm-13.395.004c.278.96.645 1.957 1.1 2.98-.45 1.017-.812 2.01-1.086 2.964-.484-.15-.944-.318-1.37-.5-1.732-.737-2.852-1.706-2.852-2.474 0-.768 1.12-1.742 2.852-2.476.42-.18.88-.342 1.356-.494zm11.678 4.28c.265.657.49 1.312.676 1.948-.64.157-1.316.29-2.016.39.24-.375.48-.762.705-1.158.225-.39.435-.788.636-1.18zm-9.945.02c.2.392.41.783.64 1.175.23.39.465.772.705 1.143-.695-.102-1.365-.23-2.006-.386.18-.63.406-1.282.66-1.933zM17.92 16.32c.112.493.2.968.254 1.423.23 1.868-.054 3.32-.714 3.708-.147.09-.338.128-.563.128-1.012 0-2.514-.807-4.11-2.28.686-.72 1.37-1.536 2.02-2.44 1.107-.118 2.154-.3 3.113-.54zm-11.83.01c.96.234 2.006.415 3.107.532.66.905 1.345 1.727 2.035 2.446-1.595 1.483-3.092 2.295-4.11 2.295-.22-.005-.406-.05-.553-.132-.666-.38-.955-1.834-.73-3.703.054-.46.142-.944.25-1.438zm4.56.64c.44.02.89.034 1.345.034.46 0 .915-.01 1.36-.034-.44.572-.895 1.095-1.345 1.565-.455-.47-.91-.993-1.36-1.565z"/>
    </svg>
);

const iconSkills: { label: string; Icon: () => JSX.Element }[] = [
    {label: "Unity", Icon: UnityIcon},
    {label: "C#", Icon: CSharpIcon},
    {label: "Unreal Engine", Icon: UnrealIcon},
    {label: "Blender", Icon: BlenderIcon},
    {label: "React", Icon: ReactIcon},
];

const skillGroups: { label: L; items: LList }[] = [
    {
        label: {ru: "Архитектура", en: "Architecture"},
        items: {
            ru: ["модульная архитектура больших проектов", "data-driven дизайн"],
            en: ["modular architecture for large projects", "data-driven design"],
        },
    },
    {
        label: {ru: "Игровой AI", en: "Game AI"},
        items: {
            ru: ["конечные автоматы", "Behaviour Trees", "A* Pathfinding"],
            en: ["finite state machines", "Behaviour Trees", "A* Pathfinding"],
        },
    },
    {
        label: {ru: "Мультиплеер", en: "Multiplayer"},
        items: {
            ru: ["клиент-серверная архитектура", "синхронизация игроков", "собственные серверы и бекенд", "моддинг Unity-игр"],
            en: ["client-server architecture", "player synchronization", "custom servers and backend", "Unity game modding"],
        },
    },
    {
        label: {ru: "Графика и анимация", en: "Graphics and animation"},
        items: {ru: ["URP", "Spine", "шейдеры (HLSL)"], en: ["URP", "Spine", "shaders (HLSL)"]},
    },
    {
        label: {ru: "Платформы и SDK", en: "Platforms and SDKs"},
        items: {ru: ["Steamworks", "FMOD", "WebGL"], en: ["Steamworks", "FMOD", "WebGL"]},
    },
    {
        label: {ru: "Другие языки", en: "Other languages"},
        items: {ru: ["C++", "Kotlin", "Java", "Python"], en: ["C++", "Kotlin", "Java", "Python"]},
    },
    {
        label: {ru: "Веб", en: "Web"},
        items: {ru: ["React", "TypeScript", "HTML/CSS"], en: ["React", "TypeScript", "HTML/CSS"]},
    },
];

const contacts = [
    {label: "Telegram", value: "Telegram: @swathingsoap", href: "https://t.me/swathingsoap"},
];

const BIRTH_DATE = new Date(2001, 1, 25);
const EXPERIENCE_START_YEAR = 2019;

function getAge(birthDate: Date): number {
    const now = new Date();
    const age = now.getFullYear() - birthDate.getFullYear();
    const hadBirthday = now.getMonth() > birthDate.getMonth()
        || (now.getMonth() === birthDate.getMonth() && now.getDate() >= birthDate.getDate());
    return hadBirthday ? age : age - 1;
}

function yearsWord(n: number, lang: Lang): string {
    if (lang === "en") return n === 1 ? "year" : "years";
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (mod10 === 1 && mod100 !== 11) return "год";
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return "года";
    return "лет";
}

const age = getAge(BIRTH_DATE);
const yearsOfExperience = new Date().getFullYear() - EXPERIENCE_START_YEAR;

const comingSoon: L = {ru: "ранний доступ скоро", en: "Early Access soon"};

const experience: Job[] = [
    {
        company: "ChronoTactica",
        period: {ru: "Декабрь 2024 – настоящее время", en: "December 2024 – present"},
        note: {
            ru: "Студия основана командой GameLabs после её закрытия владельцем, Stillfront Group.",
            en: "Founded by the GameLabs team after the studio was closed by its owner, Stillfront Group.",
        },
        roles: [
            {
                title: "Lead Unity Developer",
                project: "Legatus",
                release: comingSoon,
                steamAppId: "4335630",
                steamHeader: "3de5c8b6c9d61ef554194f39831331720cb639dd/header.jpg",
                description: {
                    ru: "Симулятор римского легиона: жизнь лагеря и снабжение, строительство, дипломатия с племенами, тактические сражения.",
                    en: "Roman legion simulator: camp life and supplies, construction, tribal diplomacy and tactical battles.",
                },
                highlights: {
                    ru: [
                        "Заложил архитектуру проекта и отвечаю за все core-системы",
                        "Принимаю ключевые технические решения и распределяю задачи между разработчиками",
                        "Спроектировал симуляцию жизни лагеря: легионеры сами распределяются по работам, страже, строительству и отдыху на Behaviour Trees",
                        "Разработал систему строительства лагеря и глобальную карту кампании",
                    ],
                    en: [
                        "Laid down the project architecture and own all core systems",
                        "Make key technical decisions and distribute tasks across the developers",
                        "Designed the camp life simulation: legionaries assign themselves to work, guard duty, construction and rest using Behaviour Trees",
                        "Built the camp construction system and the campaign world map",
                    ],
                },
            },
            {
                title: "Lead Unity Developer",
                project: "Field Orders: Eastern Front",
                release: comingSoon,
                steamAppId: "5081740",
                steamHeader: "93ae3d1da188c22a5bbe94ba71cd3084f9567679/header.jpg",
                description: {
                    ru: "Оперативный варгейм в реальном времени о Восточном фронте Второй мировой: от полка до целых армий, снабжение по дорогам, окружения, кампания с переносом потерь и ветеранов между боями.",
                    en: "Real-time operational WWII wargame on the Eastern Front: from regiments to whole armies, road-based supply, encirclements and a campaign that carries losses and veterans between battles.",
                },
            },
        ],
    },
    {
        company: "GameLabs",
        period: {ru: "Декабрь 2022 – Декабрь 2024", en: "December 2022 – December 2024"},
        roles: [
            {
                title: "Senior Unity Developer",
                project: "Ultimate General: American Revolution",
                release: {ru: "вышла в декабре 2024", en: "released December 2024"},
                steamAppId: "1901910",
                description: {
                    ru: "Историческая стратегия-песочница о Войне за независимость США: глобальная кампания с экономикой и масштабные тактические сражения.",
                    en: "Historical sandbox strategy about the American Revolutionary War: a grand campaign with an economy and large-scale tactical battles.",
                },
                highlights: {
                    ru: [
                        "Спроектировал AI юнитов для масштабных сражений: поведение на конечных автоматах и собственная система поиска пути",
                        "Разрабатывал ключевые геймплейные системы стратегического слоя и механики альтернативных кампаний",
                        "Более 70% положительных отзывов в Steam на старте продаж",
                    ],
                    en: [
                        "Designed unit AI for large-scale battles: state machine behaviour and a custom pathfinding system",
                        "Developed key gameplay systems of the strategic layer and mechanics for alternative campaigns",
                        "Over 70% positive Steam reviews at launch",
                    ],
                },
            },
        ],
    },
    {
        company: "Mehsoft",
        period: {ru: "Май 2019 – Декабрь 2022", en: "May 2019 – December 2022"},
        roles: [
            {
                title: "Lead Unity Developer",
                project: "The Underground Man 2",
                release: {ru: "вышла в сентябре 2022", en: "released September 2022"},
                steamAppId: "1486950",
                description: {
                    ru: "Нелинейный экшен на стыке жанров: beat 'em up, платформер, point-and-click.",
                    en: "Non-linear genre-mixing action game: beat 'em up, platformer, point-and-click.",
                },
                highlights: {
                    ru: [
                        "Практически в одиночку разработал программную часть игры от прототипа до релиза",
                        "Спроектировал архитектуру боевой системы с большим набором оружия и стилей боя",
                        "Разработал AI противников для 2D- и 2.5D-уровней",
                        "Более 90% положительных отзывов в Steam",
                    ],
                    en: [
                        "Programmed the game almost single-handedly, from prototype to release",
                        "Designed the combat system architecture with a wide range of weapons and fighting styles",
                        "Built enemy AI for 2D and 2.5D levels",
                        "Over 90% positive Steam reviews",
                    ],
                },
            },
            {
                title: "Lead Unity Developer",
                project: "Call of LDPR",
                release: {ru: "вышла в январе 2020", en: "released January 2020"},
                steamAppId: "1449000",
                description: {
                    ru: "Сюжетный шутер от первого лица с открытым 3D-миром.",
                    en: "Story-driven first-person shooter with an open 3D world.",
                },
                highlights: {
                    ru: [
                        "Практически в одиночку разработал программную часть игры до релиза в Steam",
                        "Реализовал открытый мир, стрельбу и физическое взаимодействие с окружением",
                        "Построил систему диалогов и ветвящегося сюжета с несколькими концовками",
                        "75% положительных отзывов в Steam",
                    ],
                    en: [
                        "Programmed the game almost single-handedly through its Steam release",
                        "Implemented the open world, shooting and physics-based interaction with the environment",
                        "Built the dialogue system and a branching story with multiple endings",
                        "75% positive Steam reviews",
                    ],
                },
            },
            {
                title: "Lead Unity Developer",
                project: "DAOB (Deadly Auction Of Beauties)",
                description: {
                    ru: "Приложение для аукционов на стримах: генерирует персонажей по параметрам лотов.",
                    en: "Live stream auction app that generates characters from lot parameters.",
                },
                highlights: {
                    ru: [
                        "Единственный программист в команде с двумя художниками и аниматором",
                        "Спроектировал систему генерации персонажей с быстрой обработкой большого объёма 2D-графики",
                        "Написал инструменты редактора Unity для работы с ассетами",
                    ],
                    en: [
                        "Sole programmer in a team with two artists and an animator",
                        "Designed the character generation system with fast processing of large volumes of 2D art",
                        "Wrote Unity editor tools for working with assets",
                    ],
                },
            },
        ],
    },
];

const steamGames = experience.flatMap((job) => job.roles).filter((role) => role.steamAppId);

const sideProjects: Job[] = [
    {
        company: "MGE Team",
        roles: [
            {
                title: "Senior Unity Developer",
                project: "MGE",
                description: {
                    ru: "Онлайн-ивент для стримеров Twitch в формате настольной игры со множеством взаимосвязанных механик.",
                    en: "Online Twitch streamer event in a board game format with many interconnected mechanics.",
                },
                highlights: {
                    ru: ["Отвечал за игровые механики, клиент-серверную архитектуру и визуальное оформление"],
                    en: ["Owned the game mechanics, client-server architecture and visual presentation"],
                },
            },
        ],
    },
    {
        company: "MegaLadder",
        roles: [
            {
                title: "Unity Developer",
                project: "MegaLadder",
                url: "https://ladder.megabonk.su/",
                description: {
                    ru: "Соревновательная платформа для Megabonk по типу FACEIT: рейтинговые сезоны, турниры, драфт-лобби.",
                    en: "FACEIT-style competitive platform for Megabonk: ranked seasons, tournaments, draft lobbies.",
                },
                highlights: {
                    ru: [
                        "Разработал мод для Megabonk, который переносит функциональность платформы прямо в игру",
                        "Добавил в игру соревновательный мультиплеер: синхронизация игроков, собственный сервер и бекенд",
                    ],
                    en: [
                        "Built a Megabonk mod that brings the platform's features right into the game",
                        "Added competitive multiplayer to the game: player synchronization, a custom server and backend",
                    ],
                },
            },
        ],
    },
    {
        company: "NASSAL",
        roles: [
            {
                title: "Unity Developer",
                project: "NASSAL",
                url: "https://nassal.pro/",
                description: {
                    ru: "Платформа стримерского ивента по прохождению игр: аукцион игр, колесо эффектов, механики на донатах, достижения, ритм-мини-игра с генерацией нот из аудиофайла.",
                    en: "Platform for a streamer game-completion event: game auction, effect wheel, donation mechanics, achievements and a rhythm mini-game that generates notes from an audio file.",
                },
            },
        ],
    },
    {
        company: "NASRAL",
        roles: [
            {
                title: "Unity Developer",
                project: "NASRAL",
                description: {
                    ru: "Стримерский ивент по прохождению игр, предшественник NASSAL.",
                    en: "Streamer game-completion event, the predecessor of NASSAL.",
                },
            },
        ],
    },
];

const ui = {
    print: {ru: "Скачать PDF", en: "Download PDF"},
    name: {ru: "Дмитрий Ефремов", en: "Dmitry Efremov"},
    location: {ru: "удалённо или релокация", en: "remote or relocation"},
    inGamedev: {ru: "в геймдеве", en: "in gamedev"},
    aboutTitle: {ru: "О себе", en: "About"},
    about: {
        ru: `Unity-разработчик с ${EXPERIENCE_START_YEAR} года. Выпустил три игры в Steam, программную часть двух из них сделал практически в одиночку. Сейчас технический лид Legatus в ChronoTactica. Специализируюсь на архитектуре больших проектов, игровом AI и сетевом мультиплеере. Принимаю ключевые технические решения и довожу проекты до релиза.`,
        en: `Unity developer since ${EXPERIENCE_START_YEAR}. Shipped three games on Steam, programming two of them almost single-handedly. Currently the technical lead of Legatus at ChronoTactica. I specialize in large-scale project architecture, game AI and networked multiplayer. I make the key technical decisions and take projects all the way to release.`,
    },
    steamGames: {ru: "Игры в Steam", en: "Games on Steam"},
    skills: {ru: "Навыки", en: "Skills"},
    experience: {ru: "Опыт работы", en: "Experience"},
    sideProjects: {ru: "Сторонние проекты", en: "Side projects"},
    educationTitle: {ru: "Образование и языки", en: "Education and languages"},
    education: {ru: "Образование", en: "Education"},
    educationValue: {ru: "Высшее", en: "Higher education"},
    languages: {ru: "Языки", en: "Languages"},
    languagesValue: {
        ru: "Русский — родной, английский — B2 (Upper-Intermediate)",
        en: "Russian — native, English — B2 (Upper-Intermediate)",
    },
    steamStore: {ru: "в Steam", en: "on Steam"},
    steamButton: {ru: "Открыть в Steam", en: "View on Steam"},
};

function JobBlock({job, lang}: { job: Job; lang: Lang }) {
    return (
        <div className="Resume-job">
            <div className="Resume-job-header">
                <h3>{job.company}</h3>
                {job.period && <span className="Resume-period">{job.period[lang]}</span>}
            </div>
            {job.note && <p className="Resume-job-note">{job.note[lang]}</p>}
            {job.roles.map((role, i) => (
                <div className="Resume-role-block" key={i}>
                    <p className="Resume-role-title">
                        {role.title}
                        {role.project && <> · <strong>{role.project}</strong></>}
                        {role.release && <span className="Resume-release"> ({role.release[lang]})</span>}
                    </p>
                    {role.steamAppId && (
                        <div className="no-print">
                            <SteamCard appId={role.steamAppId} title={role.project} header={role.steamHeader}
                                       storeLabel={ui.steamStore[lang]} buttonLabel={ui.steamButton[lang]}/>
                        </div>
                    )}
                    {role.description && <p className="Resume-role-desc">{role.description[lang]}</p>}
                    {role.highlights && (
                        <ul className="Resume-highlights">
                            {role.highlights[lang].map((item) => <li key={item}>{item}</li>)}
                        </ul>
                    )}
                    {role.url && (
                        <a className="Resume-link" href={role.url} target="_blank"
                           rel="noreferrer">{role.url.replace(/^https?:\/\//, "")}</a>
                    )}
                </div>
            ))}
        </div>
    );
}

export default function Resume() {
    const [lang, setLang] = useState<Lang>(initialLang);

    useEffect(() => {
        document.documentElement.lang = lang;
        try {
            localStorage.setItem(LANG_KEY, lang);
        } catch {
            // Remembering the choice is a convenience only
        }
    }, [lang]);

    const ageText = lang === "ru" ? `${age} ${yearsWord(age, lang)}` : `${age} years old`;

    return (
        <div className="Resume">
            <div className="Resume-actions no-print">
                <div className="Resume-lang" role="group" aria-label="Language">
                    {(["ru", "en"] as Lang[]).map((code) => (
                        <button key={code}
                                className={`Resume-lang-btn${lang === code ? " Resume-lang-btn--active" : ""}`}
                                aria-pressed={lang === code} onClick={() => setLang(code)}>
                            {code.toUpperCase()}
                        </button>
                    ))}
                </div>
                <button className="Resume-print-btn" onClick={() => window.print()}>{ui.print[lang]}</button>
            </div>

            <div className="Resume-sheet">
                <header className="Resume-header">
                    <img className="Resume-avatar" src="/canvas.png" alt={ui.name[lang]}/>
                    <div>
                        <h1>{ui.name[lang]}</h1>
                        <p className="Resume-role">Lead / Senior Unity Developer</p>
                        <p className="Resume-meta">
                            {ageText} · {ui.location[lang]} · {yearsOfExperience}+ {yearsWord(yearsOfExperience, lang)} {ui.inGamedev[lang]}
                        </p>
                        <p className="Resume-contacts">
                            {contacts.map(({label, value, href}) => (
                                <a key={label} href={href} target="_blank" rel="noreferrer">{value}</a>
                            ))}
                        </p>
                    </div>
                </header>

                <section className="Resume-section">
                    <h2>{ui.aboutTitle[lang]}</h2>
                    <p className="Resume-about">{ui.about[lang]}</p>
                </section>

                <section className="Resume-section">
                    <h2>{ui.steamGames[lang]}</h2>
                    <div className="Resume-games">
                        {steamGames.map((role) => (
                            <a className="Resume-game" key={role.steamAppId} href={steamStoreUrl(role.steamAppId!)}
                               target="_blank" rel="noreferrer">
                                <img src={steamHeaderUrl(role.steamAppId!, role.steamHeader)} alt={role.project}
                                     loading="lazy"/>
                                <span className="Resume-game-title">{role.project}</span>
                                {role.release && <span className="Resume-game-release">{role.release[lang]}</span>}
                            </a>
                        ))}
                    </div>
                </section>

                <section className="Resume-section">
                    <h2>{ui.skills[lang]}</h2>
                    <div className="Resume-icon-skills">
                        {iconSkills.map(({label, Icon}) => (
                            <div className="Resume-icon-skill" key={label}>
                                <Icon/>
                                <span>{label}</span>
                            </div>
                        ))}
                    </div>
                    <dl className="Resume-skill-groups">
                        {skillGroups.map(({label, items}) => (
                            <React.Fragment key={label.en}>
                                <dt>{label[lang]}</dt>
                                <dd>{items[lang].join(", ")}</dd>
                            </React.Fragment>
                        ))}
                    </dl>
                </section>

                <section className="Resume-section">
                    <h2>{ui.experience[lang]}</h2>
                    <div className="Resume-timeline">
                        {experience.map((job) => (
                            <JobBlock key={job.company} job={job} lang={lang}/>
                        ))}
                    </div>
                </section>

                <section className="Resume-section">
                    <h2>{ui.sideProjects[lang]}</h2>
                    <div className="Resume-timeline">
                        {sideProjects.map((job) => (
                            <JobBlock key={job.company} job={job} lang={lang}/>
                        ))}
                    </div>
                </section>

                <section className="Resume-section">
                    <h2>{ui.educationTitle[lang]}</h2>
                    <dl className="Resume-skill-groups">
                        <dt>{ui.education[lang]}</dt>
                        <dd>{ui.educationValue[lang]}</dd>
                        <dt>{ui.languages[lang]}</dt>
                        <dd>{ui.languagesValue[lang]}</dd>
                    </dl>
                </section>
            </div>
        </div>
    );
}
